// Applications page for admin — read-only view using the same card infrastructure as the user app
// Supports search, status filter, sort, and pagination. No mutations.

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebouncedSearch, getErrorMessage } from '@startus/lib';
import {
  useApplications,
  useApplicationCount,
  ApplicationStatus,
  type Application,
  type ApplicationQueryParams,
} from '@startus/applications';
import {
  ApplicationCard,
  ApplicationGroup,
  ApplicationsToolbar,
  FilterChips,
  Pagination,
  EmptyState,
  type ApplicationCardLabels,
  type SortOption,
} from '@startus/applications-ui';
import { StatusBar, type StatusConfig } from '@startus/startup-form';
import applicationEmptyImg from '@startus/ui/src/assets/images/application-empty.png';

// Shared with web — same colors and labelKeys (resolved via common namespace in StatusBar)
const APPLICATION_STATUS_CONFIG: StatusConfig[] = [
  {
    value: ApplicationStatus.IN_PROGRESS,
    color: 'bg-yellow-400',
    labelKey: 'applicationStatus.PROCESS',
  },
  { value: ApplicationStatus.SENT, color: 'bg-[#42BEFC]', labelKey: 'applicationStatus.SENT' },
  {
    value: ApplicationStatus.WAITING_RESPONSE,
    color: 'bg-green-400',
    labelKey: 'applicationStatus.WAITING',
  },
  {
    value: ApplicationStatus.NOT_SELECTED,
    color: 'bg-red-400',
    labelKey: 'applicationStatus.REJECTED',
  },
  {
    value: ApplicationStatus.NOT_STARTED,
    color: 'bg-white/30',
    labelKey: 'applicationStatus.TO_START',
  },
];

const PAGE_LIMIT = 10;

export function ApplicationsPage() {
  const { t } = useTranslation('admin');
  const { state } = useLocation();
  const navigate = useNavigate();

  const locationState = state as { startupId?: string; startup?: { name: string } } | null;
  const startupName = locationState?.startup?.name ?? null;
  const startupId = locationState?.startupId ?? null;

  const handleBack = () => {
    if (locationState?.startup) {
      navigate('/admin/startups', { state: { startup: locationState.startup }, replace: true });
    } else {
      navigate(-1);
    }
  };

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedSearch(searchInput);

  // Pagination + filters + sort
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | undefined>(undefined);
  const [activeSortIndex, setActiveSortIndex] = useState(0);

  // Card expansion — view only, no editing
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const sortOptions: SortOption[] = useMemo(
    () => [
      { label: t('applications.sort.endDateAsc'), sortBy: 'endDate', sortOrder: 'asc' },
      { label: t('applications.sort.endDateDesc'), sortBy: 'endDate', sortOrder: 'desc' },
      { label: t('applications.sort.lastEdited'), sortBy: 'createdAt', sortOrder: 'desc' },
    ],
    [t]
  );

  const activeSort = sortOptions[activeSortIndex];

  const queryParams: ApplicationQueryParams = useMemo(
    () => ({
      page,
      limit: PAGE_LIMIT,
      companyId: startupId ?? undefined,
      status: statusFilter,
      search: debouncedSearch || undefined,
      sortBy: activeSort.sortBy,
      sortOrder: activeSort.sortOrder,
    }),
    [page, startupId, statusFilter, debouncedSearch, activeSort]
  );

  const { applications, meta, loading, error } = useApplications(queryParams);
  const { data: countData } = useApplicationCount(startupId ?? undefined);

  const statusLabels: Record<ApplicationStatus, string> = useMemo(
    () => ({
      [ApplicationStatus.NOT_STARTED]: t(`applications.status.${ApplicationStatus.NOT_STARTED}`),
      [ApplicationStatus.IN_PROGRESS]: t(`applications.status.${ApplicationStatus.IN_PROGRESS}`),
      [ApplicationStatus.SENT]: t(`applications.status.${ApplicationStatus.SENT}`),
      [ApplicationStatus.WAITING_RESPONSE]: t(
        `applications.status.${ApplicationStatus.WAITING_RESPONSE}`
      ),
      [ApplicationStatus.NOT_SELECTED]: t(`applications.status.${ApplicationStatus.NOT_SELECTED}`),
    }),
    [t]
  );

  // Only view-relevant labels; edit-only fields are empty strings (never rendered in view mode)
  const cardLabels: ApplicationCardLabels = useMemo(
    () => ({
      statusLabels,
      closeDate: t('applications.card.closeDate'),
      country: t('applications.card.country'),
      url: t('applications.card.url'),
      startDate: t('applications.card.startDate'),
      endDate: t('applications.card.endDate'),
      progress: t('applications.card.progress'),
      editedByPlaceholder: t('applications.card.editedBy'),
      clickToEdit: '',
      newApplication: '',
      save: '',
      urlError: '',
      nameError: '',
      saveError: '',
    }),
    [t, statusLabels]
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    setPage(1);
  }, []);

  const handleStatusFilterToggle = useCallback((status: ApplicationStatus) => {
    setStatusFilter((prev) => (prev === status ? undefined : status));
    setPage(1);
  }, []);

  const handleClearFilter = useCallback(() => {
    setStatusFilter(undefined);
    setPage(1);
  }, []);

  const handleSortChange = useCallback((index: number) => {
    setActiveSortIndex(index);
    setPage(1);
  }, []);

  const handleToggle = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const groupedApplications = useMemo(() => {
    if (!statusFilter) return null;
    return { [statusFilter]: applications } as Partial<Record<ApplicationStatus, Application[]>>;
  }, [statusFilter, applications]);

  return (
    <div className="container mx-auto max-w-3xl">
      <div className="card bg-[#1E1E1E] shadow-xl">
        <div className="card-body">
          {/* Header: back button + startup name */}
          <div className="mb-4">
            <button
              className="btn btn-ghost btn-sm gap-1 mb-2"
              onClick={handleBack}
              aria-label={t('applications.back')}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span className="hidden sm:inline">{t('applications.back')}</span>
            </button>
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#F94A3D] shrink-0" />
              <h1 className="card-title text-4xl font-bold">
                {startupName ?? t('applications.title')}
              </h1>
            </div>
          </div>

          {!startupId ? (
            <div className="flex items-center justify-center h-64 text-base-content/50">
              {t('applications.noStartupSelected')}
            </div>
          ) : (
            <div className="w-full space-y-4">
              {/* Toolbar: search + sort + filter */}
              <ApplicationsToolbar
                searchValue={searchInput}
                onSearchChange={handleSearchChange}
                sortOptions={sortOptions}
                activeSortIndex={activeSortIndex}
                onSortChange={handleSortChange}
                statusFilter={statusFilter}
                onStatusFilterToggle={handleStatusFilterToggle}
                statusLabels={statusLabels}
                searchPlaceholder={t('applications.search')}
                sortLabel={t('applications.sort.label')}
                filterLabel={t('applications.filter.label')}
              />

              {/* Active filter chip */}
              <FilterChips
                filter={statusFilter}
                label={statusFilter ? statusLabels[statusFilter] : ''}
                onClear={handleClearFilter}
              />

              {/* Application status summary bar */}
              {countData && (
                <StatusBar
                  statuses={APPLICATION_STATUS_CONFIG}
                  counts={countData.byStatus}
                  titleKey="applicationStatus.title"
                />
              )}

              {/* Loading */}
              {loading && (
                <div className="flex items-center justify-center h-64">
                  <span className="loading loading-spinner loading-lg" />
                </div>
              )}

              {/* Error */}
              {error && !loading && (
                <div role="alert" className="alert alert-error">
                  <span>{getErrorMessage(error, t)}</span>
                </div>
              )}

              {/* Content */}
              {!loading && !error && (
                <>
                  {applications.length === 0 && debouncedSearch ? (
                    <div className="flex items-center justify-center h-32 text-base-content/50">
                      {t('applications.noResults')}
                    </div>
                  ) : applications.length === 0 ? (
                    <EmptyState
                      description={t('applications.empty')}
                      illustration={
                        <img
                          src={applicationEmptyImg}
                          alt=""
                          className="object-cover w-85 h-55 mx-auto mb-4"
                        />
                      }
                    />
                  ) : groupedApplications ? (
                    <div className="space-y-4">
                      {Object.entries(groupedApplications).map(([status, apps]) => (
                        <ApplicationGroup
                          key={status}
                          title={t(`applications.groups.${status}`)}
                          status={status as ApplicationStatus}
                          applications={apps || []}
                          expandedId={expandedId}
                          onToggle={handleToggle}
                          cardLabels={cardLabels}
                          mode="view"
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="border border-white/5 rounded-2xl p-4">
                      {applications.map((app, idx) => (
                        <ApplicationCard
                          key={app.id}
                          application={app}
                          expanded={expandedId === app.id}
                          onToggle={() => handleToggle(app.id)}
                          labels={cardLabels}
                          isLast={idx === applications.length - 1}
                          mode="view"
                        />
                      ))}
                    </div>
                  )}

                  <Pagination
                    currentPage={meta.page}
                    totalPages={meta.totalPages}
                    onPageChange={setPage}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
